import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  GraduationCap,
  Cpu,
  Phone,
  Mail,
  Globe,
  Linkedin,
  Check,
} from "lucide-react";

/**
 * Sebyx Pitch — single-page A4 portrait, print-ready.
 */
export default function PitchDoc() {
  return (
    <main
      data-testid="pitch-doc-page"
      className="min-h-screen bg-sebyx-bg text-sebyx-text font-body"
    >
      {/* Top bar (print:hidden) */}
      <header className="print:hidden sticky top-0 z-50 bg-sebyx-bg/95 backdrop-blur border-b border-sebyx-border">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <ArrowLeft
              size={16}
              className="text-sebyx-accent group-hover:-translate-x-0.5 transition-transform"
            />
            <span className="block w-2 h-2 bg-sebyx-accent" />
            <span className="font-display text-xl font-semibold tracking-tight">
              Sebyx
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
              Sebyx · One-Page Pitch
            </span>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="pitch-print-btn"
              className="px-5 py-2 bg-sebyx-accent text-sebyx-bg font-semibold text-sm hover:bg-[#3FA097] transition-colors"
            >
              Print / Save as PDF
            </button>
          </div>
        </div>
      </header>

      <div className="print:hidden max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-2">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted leading-relaxed">
          Tip · Print dialog: <span className="text-sebyx-accent">Layout = Portrait</span>,
          {" "}Margins = <span className="text-sebyx-accent">None</span>, Scale = 100%, Background graphics = <span className="text-sebyx-accent">On</span>, Headers/footers = <span className="text-sebyx-accent">Off</span>. Outputs a single A4 page.
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 print:p-0 print:max-w-none">
        <OnePagerPage />
      </div>
    </main>
  );
}

function OnePagerPage() {
  return (
    <section
      data-testid="pitch-page-01"
      className="relative bg-sebyx-bg border border-sebyx-border sebyx-corner overflow-hidden print:border-0"
      style={{
        width: "210mm",
        height: "297mm",
        margin: "0 auto",
        padding: "12mm 14mm",
      }}
    >
      <div className="absolute inset-0 sebyx-grid-bg opacity-30 pointer-events-none" />
      <div className="relative h-full flex flex-col">
        {/* Brand bar */}
        <div className="flex items-center justify-between pb-3 border-b border-sebyx-border">
          <div className="flex items-center gap-2">
            <span className="block w-2.5 h-2.5 bg-sebyx-accent" />
            <span className="font-display text-xl font-semibold tracking-tight">
              Sebyx
            </span>
            <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-muted ml-2">
              / Revenue Architecture
            </span>
          </div>
          <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-muted">
            Capability Pitch · 2026
          </span>
        </div>

        {/* Headline block */}
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="block w-7 h-px bg-sebyx-accent" />
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
              Document for Industry Friends
            </span>
          </div>
          <h1 className="font-display text-[28px] font-semibold tracking-tighter leading-[1.05] text-sebyx-text">
            Sales Breakthroughs{" "}
            <span className="text-sebyx-accent italic font-light">engineered</span>{" "}
            by 18 years of{" "}
            <span className="text-sebyx-accent italic font-light">
              ground-up Field Expertise.
            </span>
          </h1>
          <p className="mt-2.5 text-[11.5px] leading-snug text-sebyx-muted max-w-4xl">
            Architecting high-scale revenue engines for ambitious{" "}
            <span className="text-sebyx-text">B2B organizations</span> across
            India. From field strategy to custom tech — built around the way
            your business actually sells.
          </p>
        </div>

        {/* KPI strip */}
        <div className="mt-3 grid grid-cols-4 gap-px bg-sebyx-border border border-sebyx-border">
          {[
            { k: "18+", v: "Years in the field" },
            { k: "3", v: "Engagement modes" },
            { k: "Pan-India", v: "Plus global advisory" },
            { k: "Founder-led", v: "End-to-end" },
          ].map((s, i) => (
            <div key={i} className="bg-sebyx-bg px-3 py-2 flex flex-col">
              <span className="font-display text-lg font-semibold text-sebyx-text leading-tight">
                {s.k}
              </span>
              <span className="font-mono text-[7px] uppercase tracking-[0.25em] text-sebyx-muted">
                {s.v}
              </span>
            </div>
          ))}
        </div>

        {/* The Opportunity + Why Sebyx */}
        <div className="mt-4 grid grid-cols-12 gap-3">
          <div className="col-span-7">
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
              The Opportunity
            </span>
            <h2 className="font-display text-base font-semibold tracking-tight mt-1 mb-1.5 text-sebyx-text">
              B2B sales in India is broken at the last mile.
            </h2>
            <ul className="space-y-1">
              {[
                "Senior sales firepower is scarce — full-time VP-Sales is a ₹60L+ commitment most SMEs can't justify.",
                "Generic playbooks don't sell complex, long-cycle, multi-stakeholder B2B deals.",
                "Reps know how to dial. Few can navigate gatekeepers or build CXO-level professional equity.",
                "Founders end up running sales themselves — capping growth at their own calendar.",
              ].map((s, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[10.5px] text-sebyx-text/90 leading-snug"
                >
                  <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent mt-1.5 shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-5 border-l-2 border-sebyx-accent pl-3 bg-sebyx-surface/40 py-2">
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
              How Sebyx Engages
            </span>
            <p className="mt-1 text-[10.5px] text-sebyx-text/90 leading-snug">
              Sebyx productizes 18 years of revenue architecture into three
              interlocking offerings — senior leadership where judgement
              matters, structured coaching where capability compounds, and
              custom tech where scale matters.
            </p>
            <ul className="mt-2 space-y-0.5 text-[10px] text-sebyx-muted">
              {[
                "Founder-led across the engagement",
                "Outcome-led — designed for closes",
                "Long-cycle native — high-consideration sales",
                "Custom-built around your motion",
              ].map((s) => (
                <li key={s} className="flex items-center gap-2">
                  <Check
                    size={9}
                    strokeWidth={2.5}
                    className="text-sebyx-accent shrink-0"
                  />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Three Pillars */}
        <div className="mt-4">
          <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
            What Sebyx Does · Three Pillars
          </span>
          <div className="mt-1.5 grid grid-cols-3 gap-2">
            <Pillar
              code="P1"
              Icon={Briefcase}
              title="BDaaS & Fractional Sales Leadership"
              body="Plug-and-play revenue office. Sebyx owns strategy, hiring, pipeline, and operating cadence — without the full-time VP cost. 3–12 month retainers."
              points={[
                "Market entry & territory build-out",
                "Team hiring + ramp + comp design",
                "Outbound + channel demand gen",
              ]}
            />
            <Pillar
              code="P2"
              Icon={GraduationCap}
              title="Sebyx Academy — Coaching & Mentorship"
              body="Structured curriculum that meets people where they are. From rep toolkits to founder mentorship — self-paced, cohorts, 1-on-1, or in-house custom programs."
              points={[
                "Reps · scripts, cadences, daily rhythm",
                "Managers · complex B2B + negotiation",
                "Founders · 1-on-1 mentorship + scorecard",
              ]}
            />
            <Pillar
              code="P3"
              Icon={Cpu}
              title="Custom Tech for Revenue Problems"
              body="Off-the-shelf tools rarely match how a real business sells. Sebyx designs and ships small, focused tech that solves a specific revenue bottleneck."
              points={[
                "Bespoke workflow tools",
                "Sales enablement infrastructure",
                "Embedded integrations into your stack",
              ]}
            />
          </div>
        </div>

        {/* Founder strip */}
        <div className="mt-4 border border-sebyx-border bg-sebyx-surface/40 p-3 grid grid-cols-12 gap-3">
          <div className="col-span-4">
            <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-accent">
              The Founder
            </span>
            <h3 className="font-display text-base font-semibold tracking-tight mt-0.5 text-sebyx-text leading-tight">
              Mandar Prabhudesai
            </h3>
            <p className="font-mono text-[8px] tracking-[0.25em] uppercase text-sebyx-muted mt-0.5">
              Founder & Strategic Architect
            </p>
          </div>
          <div className="col-span-8 text-[10px] text-sebyx-text/90 leading-snug">
            18 years navigating the "Last Mile" of Indian business — the long,
            judgement-heavy gap between an interesting product and a closed
            institutional deal. Track record at{" "}
            <span className="text-sebyx-accent">Osstem India</span> (sole
            architect of South India expansion),{" "}
            <span className="text-sebyx-accent">Alpha-Bio Tec (Danaher)</span>,
            and as founder of{" "}
            <span className="text-sebyx-accent">Serio Healthcare</span>. Sebyx
            is operated as a brand of Serio Healthcare.
          </div>
        </div>

        {/* Engagement modes */}
        <div className="mt-4">
          <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
            Engagement Modes
          </span>
          <div className="mt-1.5 grid grid-cols-4 gap-1.5">
            {[
              {
                c: "E1",
                t: "Strategy Audit",
                d: "1 week · written audit + 90-day fix roadmap",
              },
              {
                c: "E2",
                t: "Fractional / BDaaS",
                d: "3–12 mo retainer · Sebyx runs sales end-to-end",
              },
              {
                c: "E3",
                t: "Sebyx Academy",
                d: "Self-paced · cohorts · 1-on-1 · custom in-house",
              },
              {
                c: "E4",
                t: "Custom Tech Build",
                d: "Scoped per brief · a working, owned artifact",
              },
            ].map((e) => (
              <div
                key={e.c}
                className="border border-sebyx-border bg-sebyx-bg p-2"
              >
                <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-accent">
                  {e.c}
                </span>
                <div className="font-display text-[11.5px] font-semibold text-sebyx-text leading-tight mt-0.5">
                  {e.t}
                </div>
                <div className="text-[9.5px] text-sebyx-muted leading-snug mt-1">
                  {e.d}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA + Contact */}
        <div className="mt-auto pt-4">
          <div className="border border-sebyx-accent bg-sebyx-surface/40 p-3 grid grid-cols-12 gap-3 items-center">
            <div className="col-span-7">
              <div className="flex items-center gap-2 mb-1">
                <span className="block w-5 h-px bg-sebyx-accent" />
                <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
                  Next Step
                </span>
              </div>
              <h3 className="font-display text-base font-semibold tracking-tight text-sebyx-text leading-tight">
                Know someone who fits? Make an intro.
              </h3>
              <p className="text-[10px] text-sebyx-text/90 leading-snug mt-1">
                Commercials are scoped to the brief. Reply with a name + a
                one-line context, and Sebyx will take it from there.
              </p>
            </div>
            <div className="col-span-5 grid grid-cols-2 gap-x-2 gap-y-1.5 text-[10px]">
              <Contact icon={Phone} label="Direct" value="+91 91084 36667" />
              <Contact icon={Mail} label="Email" value="contact@sebyx.com" />
              <Contact icon={Globe} label="Web" value="www.sebyx.com" />
              <Contact
                icon={Linkedin}
                label="LinkedIn"
                value="/in/mandarprabhudesai"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-2 mt-2 border-t border-sebyx-border flex items-center justify-between text-sebyx-muted font-mono text-[7.5px] tracking-[0.25em] uppercase">
          <span>contact@sebyx.com · +91 91084 36667 · www.sebyx.com</span>
          <span>Brand of Serio Healthcare · GSTIN 29APZPP8620D1Z8</span>
        </div>
      </div>
    </section>
  );
}

function Pillar({ code, Icon, title, body, points }) {
  return (
    <div className="border border-sebyx-border bg-sebyx-surface/30 p-2.5 flex flex-col">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-6 h-6 border border-sebyx-accent flex items-center justify-center shrink-0">
          <Icon size={11} className="text-sebyx-accent" />
        </div>
        <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-accent">
          {code}
        </span>
      </div>
      <h3 className="font-display text-[12.5px] font-semibold tracking-tight text-sebyx-text leading-tight">
        {title}
      </h3>
      <p className="text-[9.5px] text-sebyx-muted leading-snug mt-1">{body}</p>
      <ul className="mt-1.5 space-y-0.5">
        {points.map((p, i) => (
          <li
            key={i}
            className="flex items-start gap-1.5 text-[9.5px] text-sebyx-text/90 leading-snug"
          >
            <span className="block w-1 h-1 rotate-45 bg-sebyx-accent mt-1.5 shrink-0" />
            {p}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Contact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5">
      <div className="w-5 h-5 border border-sebyx-border bg-sebyx-bg flex items-center justify-center shrink-0">
        <Icon size={9} className="text-sebyx-accent" />
      </div>
      <div className="leading-tight">
        <div className="font-mono text-[6.5px] tracking-[0.25em] uppercase text-sebyx-muted">
          {label}
        </div>
        <div className="font-display text-[10px] text-sebyx-text leading-tight">
          {value}
        </div>
      </div>
    </div>
  );
}
